<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Une photo 360° est une image sphérique qui capture simultanément une
            vue horizontale complète à 360° et une vue verticale à 180° — créant
            ainsi un enregistrement photographique de chaque direction depuis un
            seul point dans l'espace. Contrairement à une photo classique qui
            capture une tranche rectangulaire de votre champ de vision, la photo
            360° capture l'intégralité de l'environnement autour du photographe.
            Lorsque vous la visionnez, vous pouvez faire pivoter la vue, zoomer
            et regarder dans n'importe quelle direction — y compris directement
            en haut et en bas.
          </p>
        </section>

        <section>
          <h2>Comment se crée une photo 360°</h2>

          <h3>Le processus technique</h3>
          <p>
            Les photos 360° sont créées grâce à un procédé appelé
            <strong>assemblage d'images</strong> (ou stitching). Plusieurs
            photographies superposées — prises dans différentes directions
            depuis le même point — sont alignées mathématiquement et fondues
            pour créer une sphère homogène. Le fichier résultant est
            généralement stocké sous forme d'image équirectangulaire : un
            fichier plat et rectangulaire (ratio 2:1) qui représente la surface
            sphérique projetée sur un plan, comme une carte du monde représente
            la Terre ronde sur une page plate.
          </p>
          <p>
            Les caméras 360° dédiées (Insta360, Ricoh Theta…) font cela
            automatiquement grâce à deux objectifs fisheye qui capturent chacun
            environ 220° de champ. Les applications smartphone comme Travvir
            obtiennent le même résultat en guidant la capture de plusieurs
            images séquentielles et en réalisant l'assemblage par logiciel.
          </p>

          <h3>Le format équirectangulaire expliqué</h3>
          <p>
            Si vous téléchargez un fichier photo 360° et l'ouvrez dans une
            visionneuse classique, l'image paraît déformée et étirée — surtout
            en haut et en bas. Cette distorsion est le résultat naturel de la
            projection d'une sphère sur une surface plane. Le bord gauche et le
            bord droit de l'image représentent en réalité le même point — le
            regard directement vers l'arrière.
          </p>
        </section>

        <section>
          <h2>Comment visualiser une photo 360°</h2>
          <p>
            Pour visualiser correctement une photo 360°, vous avez besoin d'un
            visualiseur sphérique qui re-projette le format équirectangulaire
            sur l'intérieur d'une sphère virtuelle. Vous vous retrouvez alors au
            centre de cette sphère et regardez vers l'extérieur, exactement
            comme si vous étiez sur le lieu de prise de vue.
          </p>
          <ul>
            <li>
              <strong>Facebook</strong> — détecte et affiche nativement les
              photos 360° dans un visualiseur interactif.
            </li>
            <li>
              <strong>Google Street View</strong> — prend en charge les photos
              360° conformes aux spécifications Google.
            </li>
            <li>
              <strong>Travvir</strong> — intègre un visualiseur web optimisé
              pour les visites virtuelles.
            </li>
            <li>
              <strong>Casques VR</strong> — affichent les photos 360° en
              expérience pleinement immersive.
            </li>
          </ul>
        </section>

        <section>
          <h2>Photo 360° vs photo panoramique : quelle différence ?</h2>
          <p>
            Une photo panoramique capture une bande horizontale large — souvent
            180° ou plus — mais reste une image plate qui ne vous permet pas de
            regarder vers le haut ou vers le bas. Une photo 360° est une sphère
            complète : vous pouvez regarder dans toutes les directions, y
            compris le plafond et le sol. C'est cette couverture sphérique
            totale qui permet de créer des visites virtuelles immersives.
          </p>
        </section>

        <section>
          <h2>Créer des photos 360° avec Travvir</h2>
          <p>
            Avec Travvir, vous n'avez pas besoin d'une caméra 360° dédiée. Votre
            smartphone suffit : l'IA guide la capture image par image, détecte
            les zones manquantes et assemble automatiquement le panorama
            sphérique en 2 à 5 minutes. Le résultat est une photo 360° prête à
            être intégrée dans une visite virtuelle interactive.
          </p>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>Quelle résolution faut-il pour une photo 360° de qualité ?</h3>
          <p>
            Un minimum de 8 000 × 4 000 pixels (32 mégapixels) est recommandé
            pour une photo 360° nette en visualisation normale. Les smartphones
            récents produisent des photos dépassant cette résolution.
          </p>

          <h3>Peut-on créer une photo 360° sans caméra spéciale ?</h3>
          <p>
            Oui. Les applications comme Travvir assemblent plusieurs photos
            prises avec votre smartphone pour créer un panorama sphérique
            complet sans aucun équipement dédié.
          </p>

          <h3>
            Une photo 360° est-elle la même chose qu'une visite virtuelle ?
          </h3>
          <p>
            Pas tout à fait. Une photo 360° est une image sphérique unique. Une
            visite virtuelle est un ensemble de plusieurs photos 360° reliées
            par des hotspots de navigation, permettant de se déplacer d'une
            pièce à l'autre de manière interactive.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/taille-images-visite-virtuelle-360">Taille et Résolution des Images pour Visites Virtuelles 360° 2026</a></li>
          <li><a href="/blog/fr/photos-360-avec-smartphone">Comment Prendre des Photos 360° avec un Smartphone : Guide Complet</a></li>
          <li><a href="/blog/fr/partager-photo-360-reseaux-sociaux">Comment Partager des Photos 360° sur les Réseaux Sociaux 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-debutants">Visite Virtuelle 360° pour Débutants : Créez votre 1er Tour depuis le Téléphone</a></li>
          <li><a href="/blog/fr/panorama-vs-visite-virtuelle-360">Panorama Smartphone vs Visite Virtuelle 360° : Quelle Différence ? 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          <li><a href="/blog/fr/corriger-photos-360-floues">Photos 360° Floues : 6 Causes et Solutions Concrètes 2026</a></li>
          <li><a href="/blog/fr/prendre-photos-360-smartphone">Comment Prendre des Photos 360° avec son Smartphone : Guide Technique 2026</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find((p) => p.slug === "qu-est-ce-photo-360");

useBlogSeo({
  title: "Qu'est-ce qu'une Photo 360° ? Guide Complet 2026 | Travvir",
  description:
    "Tout savoir sur la photo 360° : définition, format équirectangulaire, comment la créer avec un smartphone et comment l'utiliser dans une visite virtuelle.",
  keywords:
    "photo 360 définition, qu est ce qu une photo 360, photo sphérique, équirectangulaire, photo panoramique 360, créer photo 360 smartphone",
  slug: "qu-est-ce-photo-360",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "Quelle résolution faut-il pour une photo 360° de qualité ?",
      answer:
        "Un minimum de 8 000 × 4 000 pixels est recommandé. Les smartphones récents dépassent généralement cette résolution.",
    },
    {
      question: "Peut-on créer une photo 360° sans caméra spéciale ?",
      answer:
        "Oui. Travvir assemble plusieurs photos de smartphone pour créer un panorama sphérique complet sans équipement dédié.",
    },
  ],
});

</script>
